import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
    Table,
    Row,
    Col,
    Button,
    Tooltip,
    Space,
    Tag,
    message,
    PageHeader,
    Modal,
    Descriptions,
    Input,
    DatePicker,
    Segmented,
} from "antd";
import dayjs from "dayjs";
import { useSelector, useDispatch } from "react-redux";
import {
    getLeavesRequestRequestData,
    updateLeaveRequestRequestData,
} from "../../redux/mail/actionCreator";
import { Main } from "../styled";

const { confirm } = Modal;
const { RangePicker } = DatePicker;

const STATUS_COLOR = {
    SUBMITTED: "blue",
    APPROVED: "green",
    DECLINED: "red",
};

const LEAVE_TYPE_COLOR = {
    CASUAL: "geekblue",
    SICK: "gold",
    EMERGENCY: "volcano",
};

export default function LeaveHistoryLikeUI() {
    const dispatch = useDispatch();

    const [searchText, setSearchText] = useState("");
    const [dateRange, setDateRange] = useState(null);
    const [statusFilter, setStatusFilter] = useState("TO_VERIFY");

    const [statusOverride, setStatusOverride] = useState({});
    const [pendingId, setPendingId] = useState(null);

    const [mailPreviewOpen, setMailPreviewOpen] = useState(false);
    const [mailPreviewRecord, setMailPreviewRecord] = useState(null);

    const [attachmentOpen, setAttachmentOpen] = useState(false);
    const [attachmentUrl, setAttachmentUrl] = useState("");
    const [attachmentType, setAttachmentType] = useState("");
    const [attachmentName, setAttachmentName] = useState("");

    const API_BASE = process.env.REACT_APP_API_ENDPOINT;

    const normalizeFileAddress = (fileaddress = "", filename = "") => {
        const addr = (fileaddress || "").toString().trim();
        const safeFilename = (filename || "").toString().trim();

        if (addr.startsWith("compose-mail/")) return addr;

        if (addr.includes("compose-mail/")) {
            const idx = addr.toLowerCase().lastIndexOf("compose-mail/");
            return addr.substring(idx).replace(/\\/g, "/");
        }

        const lower = addr.toLowerCase();
        const idxPublic = lower.lastIndexOf("public");
        if (idxPublic !== -1) {
            let after = addr.substring(idxPublic + "public".length);
            after = after.replace(/^[/\\]+/, "");
            after = after.replace(/\\/g, "/");
            return after;
        }

        if (safeFilename && safeFilename !== "N/A") return `compose-mail/${safeFilename}`;

        return null;
    };

    const buildPublicUrl = (fileaddress, filename) => {
        const rel = normalizeFileAddress(fileaddress, filename);
        if (!rel) return "";
        return `${API_BASE}/public/${rel}`;
    };

    const getFileKind = (filename = "") => {
        const lower = (filename || "").toLowerCase();
        if (lower.endsWith(".pdf")) return "pdf";
        if (
            lower.endsWith(".png") ||
            lower.endsWith(".jpg") ||
            lower.endsWith(".jpeg") ||
            lower.endsWith(".webp") ||
            lower.endsWith(".gif")
        )
            return "image";
        return "other";
    };

    const openMailPreview = (record) => {
        setMailPreviewRecord(record);
        setMailPreviewOpen(true);
    };

    const closeMailPreview = () => {
        setMailPreviewOpen(false);
        setMailPreviewRecord(null);
    };

    const openAttachmentPreview = (filename, fileaddress) => {
        const safeName = filename && filename !== "N/A" ? filename : "";
        const url = buildPublicUrl(fileaddress, safeName);

        if (!url) {
            message.info("No attachment available.");
            return;
        }

        const kind = getFileKind(safeName);

        setAttachmentUrl(url);
        setAttachmentType(kind);
        setAttachmentName(safeName || "Attachment");
        setAttachmentOpen(true);
    };

    const closeAttachmentPreview = () => {
        setAttachmentOpen(false);
        setAttachmentUrl("");
        setAttachmentType("");
        setAttachmentName("");
    };

    const { LeaveRequestData, UpdateLeaveReqRes, loading } = useSelector((state) => ({
        LeaveRequestData: state.LeaveRequestRes?.data ?? [],
        UpdateLeaveReqRes:
            state.UpdateLeaveReqRes?.data ?? state.UpdateLeaveRequestRes?.data ?? null,
        loading: state.LeaveRequestRes?.loading ?? false,
    }));

    useEffect(() => {
        dispatch(getLeavesRequestRequestData());
    }, [dispatch]);

    useEffect(() => {
        if (!UpdateLeaveReqRes) return;

        if (UpdateLeaveReqRes?.Error === false) {
            message.success("Leave request updated");
            dispatch(getLeavesRequestRequestData());
            setPendingId(null);
            return;
        }

        if (UpdateLeaveReqRes?.Error === true) {
            message.error(UpdateLeaveReqRes?.Message || "Update failed");
            if (pendingId != null) {
                setStatusOverride((prev) => {
                    const copy = { ...prev };
                    delete copy[pendingId];
                    return copy;
                });
            }
            setPendingId(null);
        }
    }, [UpdateLeaveReqRes, dispatch, pendingId]);

    const handleUpdate = useCallback(
        (id, tag, totalLeaves) => {
            const actionText = tag === "APPROVED" ? "Accept" : "Reject";

            confirm({
                title: `Confirm ${actionText}`,
                content: `Are you sure you want to ${actionText.toLowerCase()} this leave request?`,
                okText: actionText,
                okButtonProps: tag === "DECLINED" ? { danger: true } : { type: "primary" },
                cancelText: "Cancel",
                onOk() {
                    setStatusOverride((prev) => ({ ...prev, [id]: tag }));
                    setPendingId(id);
                    dispatch(updateLeaveRequestRequestData({ id, tag, totalLeaves }));
                },
            });
        },
        [dispatch]
    );

    // ---- Helpers for filtering ----
    const normalize = (v) => (v ?? "").toString().toLowerCase().trim();

    const overlapsRange = (from, to, start, end) => {
        if (!start || !end) return true;
        if (!from && !to) return false;

        const a = from ? dayjs(from) : null;
        const b = to ? dayjs(to) : a;

        const left = a && a.isValid() ? a : null;
        const right = b && b.isValid() ? b : left;

        if (!left || !right) return false;

        return !(right.isBefore(start, "day") || left.isAfter(end, "day"));
    };

    // ---- Build rows + apply filters ----
    const dataSource = useMemo(() => {
        const rawList = Array.isArray(LeaveRequestData)
            ? LeaveRequestData
            : LeaveRequestData?.payload || LeaveRequestData?.data || [];

        const list = Array.isArray(rawList) ? rawList : [];

        const q = normalize(searchText);
        const start = dateRange?.[0] || null;
        const end = dateRange?.[1] || null;

        return list
            .map((r, idx) => {
                const dates = Array.isArray(r?.leaveDates) ? r.leaveDates : [];
                const from = dates[0] || null;
                const to = dates.length ? dates[dates.length - 1] : null;

                const id = r?.id ?? `row-${idx}`;
                const status = (statusOverride[id] || r?.status || "SUBMITTED")
                    .toString()
                    .toUpperCase();

                const filename = r?.filename && r.filename.trim() !== "" ? r.filename : "N/A";
                const fileaddress = normalizeFileAddress(r?.fileaddress, filename);
                const leaveType = (r?.LeaveType
                    || r?.LeaveType
                    || r?.type || "").toString().toUpperCase();
                return {
                    key: id,
                    id,
                    name: r?.personName ?? r?.person?.name ?? "NA",
                    reason: r?.reason ?? r?.subject ?? "-",
                    leaveType: leaveType || "-",
                    from,
                    to,
                    days: typeof r?.leavesTaken === "number" ? r.leavesTaken : dates.length,
                    createdOn: r?.createdOn ?? r?.createdon ?? null,
                    status,
                    subject: r?.subject ?? r?.reason ?? "-",
                    descriptionHtml: r?.description ?? "",
                    filename,
                    fileaddress,
                };
            })
            .filter((row) => {
                if (statusFilter === "TO_VERIFY") return row.status === "SUBMITTED";
                if (statusFilter === "APPROVED") return row.status === "APPROVED";
                if (statusFilter === "DECLINED") return row.status === "DECLINED";
                return true;
            })
            .filter((row) => {
                if (!q) return true;

                const hay = [
                    normalize(row.name),
                    normalize(row.reason),
                    normalize(row.subject),
                    normalize(row.leaveType),
                    normalize(row.filename),
                    normalize(dayjs(row.from).isValid() ? dayjs(row.from).format("YYYY-MM-DD") : ""),
                    normalize(dayjs(row.to).isValid() ? dayjs(row.to).format("YYYY-MM-DD") : ""),
                ].join(" ");

                return hay.includes(q);
            })
            .filter((row) => overlapsRange(row.from, row.to, start, end));
    }, [LeaveRequestData, statusOverride, statusFilter, searchText, dateRange]);

    const columns = [
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            render: (text) => <span style={{ fontWeight: 600 }}>{text}</span>,
        },

        {
            title: "Leave Type",
            dataIndex: "leaveType",
            key: "leaveType",
            width: 130,
            render: (t) => {
                const v = (t || "-").toString().toUpperCase();
                if (v === "-" || v === "NA") return <Tag>-</Tag>;
                return (
                    <Tag color={LEAVE_TYPE_COLOR[v] || "default"} style={{ fontWeight: 700 }}>
                        {v}
                    </Tag>
                );
            },
        },

        {
            title: "From",
            dataIndex: "from",
            key: "from",
            render: (v) => (v ? dayjs(v).format("D MMM YY, ddd") : "-"),
        },
        {
            title: "To",
            dataIndex: "to",
            key: "to",
            render: (v) => (v ? dayjs(v).format("D MMM YY, ddd") : "-"),
        },
        {
            title: "Days",
            dataIndex: "days",
            key: "days",
            width: 80,
        },
        {
            title: "Reason",
            dataIndex: "reason",
            key: "reason",
            render: (text) => (
                <Tooltip title={text}>
                    <span className="lh-ellipsis">{text}</span>
                </Tooltip>
            ),
        },
        {
            title: "Attachment",
            key: "attachment",
            width: 200,
            render: (_, record) => {
                const hasFile = record?.filename && record.filename !== "N/A" && record?.fileaddress;
                if (!hasFile) return <Tag>None</Tag>;

                const kind = getFileKind(record.filename);

                return (
                    <Space>
                        <Tag color="purple" style={{ margin: 0, fontWeight: 700 }}>
                            {kind.toUpperCase()}
                        </Tag>
                        <Button
                            size="small"
                            onClick={() => openAttachmentPreview(record.filename, record.fileaddress)}
                        >
                            Open
                        </Button>
                    </Space>
                );
            },
        },
        {
            title: "Applied On",
            dataIndex: "createdOn",
            key: "createdOn",
            render: (v) => (v ? dayjs(v).format("YYYY-MM-DD HH:mm") : "-"),
        },
        {
            title: "Status / Action",
            dataIndex: "status",
            key: "status",
            width: 460,
            render: (_, record) => {
                const s = record?.status;

                if (s === "SUBMITTED") {
                    return (
                        <Space>
                            <Button size="small" onClick={() => openMailPreview(record)}>
                                View Mail
                            </Button>

                            <Button
                                size="small"
                                type="primary"
                                onClick={() => handleUpdate(record.id, "APPROVED", record.days)}
                            >
                                Accept
                            </Button>

                            <Button
                                size="small"
                                danger
                                onClick={() => handleUpdate(record.id, "DECLINED")}
                            >
                                Reject
                            </Button>
                        </Space>
                    );
                }

                return (
                    <Space>
                        <Tag color={STATUS_COLOR[s] || "default"} style={{ fontWeight: 700 }}>
                            {s}
                        </Tag>
                        <Button size="small" onClick={() => openMailPreview(record)}>
                            View Mail
                        </Button>
                    </Space>
                );
            },
        },
    ];

    return (
        <>
            <PageHeader className="ninjadash-page-header-main" title="Leave Request" />
            <Main>
                <Row gutter={16}>
                    <Col span={24}>
                        <div className="lh-card">
                            <div className="lh-toolbar">
                                <Space wrap size={10}>
                                    <Input
                                        allowClear
                                        placeholder="Search by name / reason / subject / leave type / date / filename"
                                        style={{ width: 360 }}
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                    />

                                    <RangePicker value={dateRange} onChange={setDateRange} allowClear />

                                    <Segmented
                                        value={statusFilter}
                                        onChange={setStatusFilter}
                                        options={[
                                            { label: "To Verify", value: "TO_VERIFY" },
                                            { label: "Approved", value: "APPROVED" },
                                            { label: "Declined", value: "DECLINED" },
                                            { label: "All", value: "ALL" },
                                        ]}
                                    />
                                </Space>
                            </div>

                            <Table
                                columns={columns}
                                dataSource={dataSource}
                                loading={loading}
                                pagination={{ pageSize: 8 }}
                            />
                        </div>
                    </Col>
                </Row>

                <Modal
                    title="Mail Preview"
                    open={mailPreviewOpen}
                    onCancel={closeMailPreview}
                    footer={[
                        <Button key="close" type="primary" onClick={closeMailPreview}>
                            Close
                        </Button>,
                    ]}
                >
                    <Descriptions size="small" bordered column={1}>
                        <Descriptions.Item label="Name">{mailPreviewRecord?.name || "-"}</Descriptions.Item>

                        <Descriptions.Item label="Leave Type">
                            {mailPreviewRecord?.leaveType && mailPreviewRecord.leaveType !== "-" ? (
                                <Tag color={LEAVE_TYPE_COLOR[mailPreviewRecord.leaveType] || "default"} style={{ fontWeight: 700 }}>
                                    {mailPreviewRecord.leaveType}
                                </Tag>
                            ) : (
                                "-"
                            )}
                        </Descriptions.Item>

                        <Descriptions.Item label="From">
                            {mailPreviewRecord?.from ? dayjs(mailPreviewRecord.from).format("D MMM YYYY") : "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="To">
                            {mailPreviewRecord?.to ? dayjs(mailPreviewRecord.to).format("D MMM YYYY") : "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Days">{mailPreviewRecord?.days ?? "-"}</Descriptions.Item>
                        <Descriptions.Item label="Applied On">
                            {mailPreviewRecord?.createdOn
                                ? dayjs(mailPreviewRecord.createdOn).format("YYYY-MM-DD HH:mm")
                                : "-"}
                        </Descriptions.Item>
                    </Descriptions>

                    <div style={{ marginTop: 14 }}>
                        <div style={{ fontWeight: 700, marginBottom: 6 }}>Subject</div>
                        <div
                            style={{
                                border: "1px solid #e5e7eb",
                                borderRadius: 8,
                                padding: 10,
                                background: "#fafafa",
                            }}
                        >
                            {mailPreviewRecord?.subject ?? "-"}
                        </div>
                    </div>

                    <div style={{ marginTop: 14 }}>
                        <div style={{ fontWeight: 700, marginBottom: 6 }}>Description</div>
                        <div
                            style={{
                                border: "1px solid #e5e7eb",
                                borderRadius: 8,
                                padding: 10,
                                background: "#fafafa",
                            }}
                            dangerouslySetInnerHTML={{
                                __html: mailPreviewRecord?.descriptionHtml || "<p>-</p>",
                            }}
                        />
                    </div>

                    <div style={{ marginTop: 14 }}>
                        <div style={{ fontWeight: 700, marginBottom: 6 }}>Attachment</div>

                        {mailPreviewRecord?.filename &&
                            mailPreviewRecord.filename !== "N/A" &&
                            mailPreviewRecord?.fileaddress ? (
                            <Space wrap>
                                <Tag color="purple" style={{ margin: 0 }}>
                                    {mailPreviewRecord.filename}
                                </Tag>

                                <Button
                                    size="small"
                                    onClick={() =>
                                        openAttachmentPreview(mailPreviewRecord.filename, mailPreviewRecord.fileaddress)
                                    }
                                >
                                    View
                                </Button>

                                <Button
                                    size="small"
                                    onClick={() =>
                                        window.open(
                                            buildPublicUrl(mailPreviewRecord.fileaddress, mailPreviewRecord.filename),
                                            "_blank"
                                        )
                                    }
                                >
                                    Open in new tab
                                </Button>
                            </Space>
                        ) : (
                            <Tag>None</Tag>
                        )}
                    </div>
                </Modal>

                <Modal
                    title={`Attachment Preview${attachmentName ? ` - ${attachmentName}` : ""}`}
                    open={attachmentOpen}
                    onCancel={closeAttachmentPreview}
                    width={900}
                    footer={[
                        <Button key="close" type="primary" onClick={closeAttachmentPreview}>
                            Close
                        </Button>,
                        attachmentUrl ? (
                            <Button key="opennew" onClick={() => window.open(attachmentUrl, "_blank")}>
                                Open in new tab
                            </Button>
                        ) : null,
                    ]}
                >
                    {!attachmentUrl ? (
                        <div>No attachment</div>
                    ) : attachmentType === "image" ? (
                        <div style={{ textAlign: "center" }}>
                            <img
                                src={attachmentUrl}
                                alt={attachmentName}
                                style={{ maxWidth: "100%", maxHeight: "70vh", borderRadius: 10 }}
                            />
                        </div>
                    ) : attachmentType === "pdf" ? (
                        <iframe
                            title="pdf-preview"
                            src={attachmentUrl}
                            style={{
                                width: "100%",
                                height: "70vh",
                                border: "1px solid #e5e7eb",
                                borderRadius: 10,
                            }}
                        />
                    ) : (
                        <div>
                            <p>Preview not supported for this file type.</p>
                            <Button onClick={() => window.open(attachmentUrl, "_blank")}>Download / Open</Button>
                        </div>
                    )}
                </Modal>

                <style>{`
          .lh-card {
            background: #fff;
            padding: 10px;
            border-radius: 8px;
          }
          .lh-toolbar{
            margin-bottom: 10px;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          .lh-ellipsis {
            display: inline-block;
            max-width: 420px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            vertical-align: bottom;
          }
        `}</style>
            </Main>
        </>
    );
}
